import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, CheckCircle, Clock, File } from 'lucide-react';
import caseService from '../../services/case.service';
import { contentManagementService } from '../../services/contentManagement.service';

interface CasePublicData {
  case_id: string;
  cms_number: string;
  rrms_ref_number?: string | null;
  display_number?: string | null;
  fir_number?: string;
  district?: string;
  ps_name?: string;
  case_type?: string;
  case_status?: string;
  year?: number;
  cms_case_name?: string | null;
}

interface Counts {
  totalDocuments: number;
  approvedCount: number;
  pendingCount: number;
}

interface QrCaseDocument {
  document_id: number | string;
  original_file_name: string;
  file_extension?: string | null;
  file_size_bytes?: number | null;
  folder_type: string;
  document_type: string;
  page_count?: number | null;
  uploaded_by_name?: string;
  created_at: string;
  approval_status?: string;
}

function formatFileSize(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  if (kb >= 1) return `${kb.toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function parseDocumentsPayload(res: unknown): QrCaseDocument[] {
  const data = (res as { data?: { data?: unknown } })?.data?.data ?? (res as { data?: unknown })?.data;
  const payload = data as { items?: unknown[]; documents?: unknown[] } | unknown[] | null;
  const itemsRaw: unknown[] = Array.isArray(payload)
    ? payload
    : (payload as { items?: unknown[] })?.items
      ?? (payload as { documents?: unknown[] })?.documents
      ?? [];

  return itemsRaw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      document_id: r.document_id != null ? String(r.document_id) : '',
      original_file_name:
        String(r.original_file_name ?? r.file_name ?? 'Untitled document'),
      file_extension: (r.file_extension as string | null) ?? null,
      file_size_bytes:
        typeof r.file_size_bytes === 'number'
          ? r.file_size_bytes
          : typeof r.file_size_bytes === 'string' &&
              r.file_size_bytes.trim().length > 0 &&
              !Number.isNaN(Number(r.file_size_bytes))
            ? Number(r.file_size_bytes)
            : null,
      folder_type: String(r.folder_type ?? 'Case Files'),
      document_type: String(r.document_type ?? 'Document'),
      page_count: typeof r.page_count === 'number' ? r.page_count : null,
      uploaded_by_name: String(r.uploaded_by_name ?? r.author ?? '—'),
      created_at: String(r.created_at ?? new Date().toISOString()),
      approval_status: String(r.approval_status ?? 'Pending'),
    };
  });
}

function parseCasePayload(res: unknown): CasePublicData | null {
  const data = (res as { data?: { data?: unknown } })?.data?.data ?? (res as { data?: unknown })?.data;
  const wrapped = data as Record<string, unknown> | null;
  if (!wrapped) return null;
  const c: Record<string, unknown> =
    wrapped.case != null && typeof wrapped.case === 'object'
      ? (wrapped.case as Record<string, unknown>)
      : wrapped;
  if (c.case_id == null && c.cms_number == null) return null;
  return {
    case_id: String(c.case_id ?? ''),
    cms_number: String(c.cms_number ?? '—'),
    rrms_ref_number: (c.rrms_ref_number as string | null) ?? null,
    display_number: (c.display_number as string | null) ?? null,
    fir_number: c.fir_number != null ? String(c.fir_number) : undefined,
    district: c.district != null ? String(c.district) : undefined,
    ps_name: c.ps_name != null ? String(c.ps_name) : undefined,
    case_type: c.case_type != null ? String(c.case_type) : undefined,
    case_status: c.case_status != null ? String(c.case_status) : undefined,
    year: typeof c.year === 'number' ? c.year : undefined,
    cms_case_name: (c.cms_case_name as string | null) ?? null,
  };
}

const CaseQRViewPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<CasePublicData | null>(null);
  const [counts, setCounts] = useState<Counts>({ totalDocuments: 0, approvedCount: 0, pendingCount: 0 });
  const [documents, setDocuments] = useState<QrCaseDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('rrms-standalone-scroll');
    return () => document.body.classList.remove('rrms-standalone-scroll');
  }, []);

  useEffect(() => {
    document.title = 'RRMS | Case Details';
    if (!caseId) {
      setError('Invalid case ID.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const [caseRes, docsRes] = await Promise.all([
          caseService.getCaseById(caseId),
          contentManagementService.getCaseDocuments(caseId, { page: 1, limit: 200 }),
        ]);

        if (cancelled) return;

        const parsedCase = parseCasePayload(caseRes);
        if (!parsedCase) {
          setError('Case not found.');
          return;
        }

        const docs = parseDocumentsPayload(docsRes);
        const approvedCount = docs.filter((d) => d.approval_status === 'Approved').length;
        const pendingCount = docs.filter((d) => d.approval_status === 'Pending').length;

        setCaseData(parsedCase);
        setDocuments(docs);
        setCounts({
          totalDocuments: docs.length,
          approvedCount,
          pendingCount,
        });
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        setError(msg || 'Failed to load case details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const headerField = (label: string, value?: string | number | null) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.6)' }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
        {value ?? '—'}
      </span>
    </div>
  );

  const statusStyle = (status?: string) => {
    const approved = status === 'Approved';
    return {
      color: approved ? '#16a34a' : '#d97706',
      borderColor: approved ? '#bbf7d0' : '#fde68a',
      background: approved ? '#f0fdf4' : '#fffbeb',
    };
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6fb' }}>
        <span style={{ color: '#6b7280', fontSize: 15 }}>Loading case details…</span>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6fb', padding: 24 }}>
        <div style={{ textAlign: 'center', color: '#dc2626' }}>
          <p style={{ fontSize: 16, fontWeight: 600 }}>Case not found</p>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f4f6fb', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: 'max(32px, env(safe-area-inset-bottom))' }}>

      {/* Top bar */}
      <div style={{ background: '#0f2044', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>R</span>
        </div>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>RRMS</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>Records Room Management</div>
        </div>
      </div>

      {/* Case hero banner */}
      <div style={{ background: 'linear-gradient(135deg, #0f2044 0%, #1a3a6e 100%)', padding: '24px 20px 28px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -30, top: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', right: 30, bottom: -40, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

        <div style={{ display: 'inline-block', background: 'rgba(255,193,7,0.2)', border: '1px solid rgba(255,193,7,0.4)', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, color: '#ffd54f', letterSpacing: '0.08em', marginBottom: 10 }}>
          CASE RECORD
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '0.03em', marginBottom: 4 }}>
          {caseData.display_number || caseData.cms_number}
        </div>
        {caseData.rrms_ref_number && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>REF: {caseData.rrms_ref_number}</div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px 8px', marginTop: 20 }}>
          {headerField('FIR No.', caseData.fir_number)}
          {headerField('District', caseData.district)}
          {headerField('Police Station', caseData.ps_name)}
          {headerField('Case Type', caseData.case_type)}
          {headerField('Status', caseData.case_status)}
          {headerField('Year', caseData.year)}
        </div>
      </div>

      {/* Count cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, padding: '14px 16px 0' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '14px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <File size={16} color="#3b82f6" />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{counts.totalDocuments}</div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Total Documents</div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 10, padding: '14px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckCircle size={16} color="#22c55e" />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{counts.approvedCount}</div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Approved</div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 10, padding: '14px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Clock size={16} color="#f59e0b" />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{counts.pendingCount}</div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Pending Approval</div>
          </div>
        </div>
      </div>

      {/* Documents list */}
      <div style={{ padding: '12px 16px 32px' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Documents</span>
            <span style={{ marginLeft: 8, background: '#eff6ff', color: '#1d4ed8', borderRadius: 99, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
              {documents.length}
            </span>
          </div>

          {documents.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              No documents found for this case.
            </div>
          ) : (
            <div>
              {documents.map((doc, idx) => {
                const approved = doc.approval_status === 'Approved';
                const st = statusStyle(doc.approval_status);
                return (
                  <div
                    key={doc.document_id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '12px 16px',
                      borderBottom: idx < documents.length - 1 ? '1px solid #f9fafb' : 'none',
                      borderLeft: `3px solid ${approved ? '#22c55e' : '#f59e0b'}`,
                    }}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: 6, background: st.background, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                      <FileText size={16} color={approved ? '#16a34a' : '#d97706'} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', wordBreak: 'break-word' }}>
                        {doc.original_file_name}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 4 }}>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{doc.folder_type}</span>
                        {doc.document_type && <span style={{ fontSize: 11, color: '#6b7280' }}>· {doc.document_type}</span>}
                        {doc.page_count != null && <span style={{ fontSize: 11, color: '#6b7280' }}>· Pages: {doc.page_count}</span>}
                        <span style={{ fontSize: 11, color: '#6b7280' }}>· {formatFileSize(doc.file_size_bytes)}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, border: `1px solid ${st.borderColor}`, background: st.background, color: st.color }}>
                          {doc.approval_status ?? 'Pending'}
                        </span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(doc.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseQRViewPage;
